
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { LogOut } from "lucide-react";

import logo from "../assets/logo.png";

import PortalLogo from "./PortalLogo";

import useRBAC from "../hooks/useRBAC";
import usePortalFeatures from "../hooks/usePortalFeatures";
import { HIDE_INBOX_REVIEW } from "../config/portalFeatureNav";
import { notifyAuthContextChanged } from "../utils/authCache";

import "./Sidebar.css";

function NavSectionDivider() {
  return (
    <div
      style={{
        height: "1px",
        background: "rgba(255,255,255,0.08)",
        margin: "14px 10px",
      }}
    />
  );
}

function NavSectionHeader({ label }) {
  return (
    <div
      style={{
        padding: "0 14px",
        fontSize: "11px",
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        marginBottom: "8px",
      }}
    >
      {label}
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const location = useLocation();
  const { accounts, instance } = useMsal();

  const handleNavClick = () => {
    onNavigate?.();
  };

  const handleSignOut = async () => {
    notifyAuthContextChanged();
    const currentUser = accounts?.[0];
    try {
      await instance.logoutRedirect({ account: currentUser });
    } catch {
      try {
        await instance.logoutPopup({ account: currentUser });
        window.location.href = "/";
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
  };

  const { isAdmin, loading: rbacLoading } = useRBAC();
  const { navigation, loading: featuresLoading } = usePortalFeatures();

  const loading = rbacLoading || featuresLoading;

  const renderNavLink = (item) => (
    <Link
      key={item.resource_path}
      to={item.resource_path}
      onClick={handleNavClick}
      className={`nav-item ${
        location.pathname === item.resource_path ? "active" : ""
      }`}
    >
      {item.navLabel}
    </Link>
  );

  if (loading) {
    return (
      <div className="sidebar">
        <div className="sidebar-header">
          <PortalLogo variant="sidebar" />
        </div>

        <div
          style={{
            padding: "20px",
            color: "#94a3b8",
          }}
        >
          Loading menu...
        </div>

        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-signout"
            onClick={handleSignOut}
          >
            <LogOut size={16} />
            Sign Out
          </button>

          <div className="sidebar1-footer-logo">
            <img src={logo} alt="CFP Logo" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <PortalLogo variant="sidebar" />
      </div>

      <div className="sidebar-nav">
        <Link
          to="/"
          onClick={handleNavClick}
          className={`nav-item ${location.pathname === "/" ? "active" : ""}`}
        >
          Dashboard
        </Link>

        {navigation.topLevel.map(renderNavLink)}

        {navigation.groups.map((group) => {
          const items = HIDE_INBOX_REVIEW
            ? group.items.filter((item) => item.resource_code !== "DOC_INDEX_HUB")
            : group.items;
          if (!items.length) return null;
          return (
            <div key={group.groupLabel} className="nav-group">
              <NavSectionDivider />
              <NavSectionHeader label={group.groupLabel} />
              {items.map(renderNavLink)}
            </div>
          );
        })}

        {isAdmin && (
          <div className="nav-group">
            <NavSectionDivider />
            <NavSectionHeader label="Administration" />

            <Link
              to="/admin-dashboard"
              onClick={handleNavClick}
              className={`nav-item ${
                location.pathname === "/admin-dashboard" ? "active" : ""
              }`}
            >
              Admin Console
            </Link>

            <Link
              to="/manage"
              onClick={handleNavClick}
              className={`nav-item ${
                location.pathname === "/manage" ? "active" : ""
              }`}
            >
              Manage
            </Link>
          </div>
        )}
      </div>

      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-signout"
          onClick={handleSignOut}
        >
          <LogOut size={16} />
          Sign Out
        </button>

        <div className="sidebar1-footer-logo">
          <img src={logo} alt="CFP Logo" />
        </div>
      </div>
    </div>
  );
}
